import React from 'react';
import { AlertTriangle, Info, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type {
  PromptReviewCandidate,
  PromptReviewDecision,
  VideoSegment,
} from '@/lib/api/contentLibrary';

interface SegmentReviewSectionProps {
  segment: VideoSegment;
  candidate?: PromptReviewCandidate | null;
  isApplying?: boolean;
  onDecision: (decision: PromptReviewDecision) => void;
  resolveReviewerName?: (userId: string) => string | null;
  onOpenMatchedSegment?: (videoId: string, segmentId: string) => void;
}

const STATUS_PENDING = 'review_pending';
const STATUS_APPROVED = 'prompt_approved';
const STATUS_LIBRARY_ONLY = 'library_only';
const STATUS_IGNORED = 'ignored';

interface StoredReview {
  status?: string | null;
  review_decision?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  matched_segment_id?: string | null;
}

const readStoredReview = (segment: VideoSegment): StoredReview | null => {
  const meta = segment.custom_metadata;
  if (!meta || typeof meta !== 'object') return null;
  const review = (meta as Record<string, unknown>).prompt_review;
  if (!review || typeof review !== 'object') return null;
  return review as StoredReview;
};

const formatReviewedAt = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTimestamp = (value?: number | null): string => {
  if (value == null || Number.isNaN(value)) return '0:00';
  const totalSeconds = Math.max(0, Math.floor(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const getResolvedStatusLabel = (
  status: string,
  decision?: string | null,
): string => {
  if (decision === 'replace_matched') return 'Replaced Duplicate';
  if (status === STATUS_APPROVED) return 'In Prompt';
  if (status === STATUS_LIBRARY_ONLY) return 'Library Only';
  if (status === STATUS_IGNORED) return 'Ignored';
  return 'Reviewed';
};

const SegmentReviewSection: React.FC<SegmentReviewSectionProps> = ({
  segment,
  candidate,
  isApplying = false,
  onDecision,
  resolveReviewerName,
  onOpenMatchedSegment,
}) => {
  const [isEditingResolved, setIsEditingResolved] = React.useState(false);

  const review = readStoredReview(segment);
  if (!review || !review.status) return null;

  const status = review.status;
  const isPending = status === STATUS_PENDING;
  const matchedSegment = candidate?.matched_segment ?? null;
  const hadMatchId = Boolean(
    matchedSegment?.segment_id || review.matched_segment_id,
  );

  const matchButtons = (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        variant="outline"
        size="sm"
        disabled={isApplying}
        onClick={() => onDecision('replace_matched')}
      >
        {isApplying && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
        Replace Duplicate
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={isApplying}
        onClick={() => onDecision('approve')}
      >
        Keep Both
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={isApplying}
        onClick={() => onDecision('ignore')}
      >
        Skip this Segment
      </Button>
    </div>
  );

  const standardButtons = (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        variant="outline"
        size="sm"
        disabled={isApplying}
        onClick={() => onDecision('approve')}
      >
        {isApplying && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
        Add Segment
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={isApplying}
        onClick={() => onDecision('library_only')}
      >
        Add to Library Only
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={isApplying}
        onClick={() => onDecision('ignore')}
      >
        Ignore
      </Button>
    </div>
  );

  const resolvedEditButtons = (
    <div className="flex items-center gap-2 flex-wrap">
      {hadMatchId && (
        <Button
          variant="outline"
          size="sm"
          disabled={isApplying}
          onClick={() => onDecision('replace_matched')}
        >
          {isApplying && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          Replace Duplicate
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={isApplying}
        onClick={() => onDecision('approve')}
      >
        {!hadMatchId && isApplying && (
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
        )}
        Add Segment
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={isApplying}
        onClick={() => onDecision('library_only')}
      >
        Add to Library Only
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={isApplying}
        onClick={() => onDecision('ignore')}
      >
        Ignore
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsEditingResolved(false)}
        disabled={isApplying}
      >
        Cancel
      </Button>
    </div>
  );

  // State 1: pending + duplicate match
  if (isPending && (matchedSegment || hadMatchId)) {
    const matchName = matchedSegment?.label || 'Existing segment';
    const videoTitle = matchedSegment?.video_title;
    const timeRange =
      matchedSegment &&
      matchedSegment.start_time != null &&
      matchedSegment.end_time != null
        ? `${formatTimestamp(matchedSegment.start_time)} – ${formatTimestamp(matchedSegment.end_time)}`
        : null;

    return (
      <div className="px-4 py-3 border-t card-border bg-amber-50 dark:bg-amber-950/30">
        <div className="flex items-start gap-3 flex-wrap">
          {matchedSegment?.thumbnail_url ? (
            <div className="w-16 shrink-0 aspect-video rounded overflow-hidden bg-zinc-800">
              <img
                src={matchedSegment.thumbnail_url}
                alt={matchName}
                className="w-full h-full object-cover"
              />
            </div>
          ) : null}
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Segment name already exists
              </span>
            </div>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
              Matches{' '}
              {matchedSegment?.video_id && matchedSegment?.segment_id && onOpenMatchedSegment ? (
                <button
                  type="button"
                  onClick={() =>
                    onOpenMatchedSegment(
                      matchedSegment.video_id as string,
                      matchedSegment.segment_id,
                    )
                  }
                  className="font-medium underline-offset-2 hover:underline focus:underline focus:outline-none"
                >
                  {matchName}
                </button>
              ) : (
                <span className="font-medium">{matchName}</span>
              )}
              {videoTitle ? <> in {videoTitle}</> : null}
              {timeRange ? <> · {timeRange}</> : null}
            </p>
          </div>
          {matchButtons}
        </div>
      </div>
    );
  }

  // State 2: pending, no match
  if (isPending) {
    return (
      <div className="px-4 py-3 border-t card-border bg-card">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Info className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium">Pending Review</span>
            <span className="text-xs text-muted-foreground truncate">
              Decide how this segment should be handled.
            </span>
          </div>
          {standardButtons}
        </div>
      </div>
    );
  }

  // State 3: resolved — collapsed chip by default, expands on Change
  const resolvedLabel = getResolvedStatusLabel(status, review.review_decision);
  const reviewedAtText = formatReviewedAt(review.reviewed_at);
  // Resolve reviewer id to a human-readable name. If the resolver returns
  // null (member not found / not yet loaded), drop the "by reviewer" part
  // rather than exposing a raw UUID to the user.
  const resolvedReviewerName = review.reviewed_by && resolveReviewerName
    ? resolveReviewerName(review.reviewed_by)
    : null;
  const metaText =
    resolvedReviewerName && reviewedAtText
      ? `Reviewed by ${resolvedReviewerName} on ${reviewedAtText}`
      : resolvedReviewerName
        ? `Reviewed by ${resolvedReviewerName}`
        : reviewedAtText
          ? `Reviewed on ${reviewedAtText}`
          : '';

  return (
    <div className="px-4 py-3 border-t card-border bg-card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span className="text-sm font-medium">{resolvedLabel}</span>
          {metaText && (
            <span className="text-xs text-muted-foreground truncate">
              {metaText}
            </span>
          )}
        </div>
        {isEditingResolved ? (
          resolvedEditButtons
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditingResolved(true)}
            disabled={isApplying}
          >
            Change
          </Button>
        )}
      </div>
    </div>
  );
};

export default SegmentReviewSection;
